<template>
  <div
    class="chart-wrapper"
    :style="{ height: height + 'px', width: width ? width + 'px' : '100%' }"
    ref="wrapperRef"
  >
    <!-- Skeleton loader while waiting for visibility -->
    <div v-if="!mountedChart" class="skeleton"></div>

    <!-- Chart mounts only when wrapper is visible and measurable -->
    <VChart
      v-else
      ref="chartRef"
      class="chart"
      :option="option"
      :theme="theme"
      autoresize
    />
  </div>
</template>

<script>
import { ref, watch, onMounted, onBeforeUnmount, onActivated, nextTick } from 'vue'
import VChart from 'vue-echarts'

export default {
  name: 'StoraChart',
  components: { VChart },
  props: {
    option: { type: Object, required: true },
    name: { type: String, default: 'Chart' },
    height: { type: Number, default: 340 },
    width: { type: [Number, String], default: '100%' },
    theme: { type: [String, Object], default: undefined },
    active: { type: Boolean, default: true } // for tab visibility
  },
  setup(props) {
    const wrapperRef = ref(null)
    const chartRef = ref(null)
    const mountedChart = ref(false)
    let io = null
    let ro = null
    let resizeRaf = null

    const canMount = () => {
      const el = wrapperRef.value
      return !!(el && el.offsetParent !== null && el.clientWidth > 0 && el.clientHeight > 0)
    }

    // Defer resize to the next frame. Calling chart.resize() synchronously
    // inside a ResizeObserver callback mutates the canvas, which can re-trigger
    // the observer; with ~12 charts on the dashboard that floods the browser's
    // RO loop budget and updates get *silently dropped* (the layout looks
    // "stuck" on window drag until a scroll re-fires it). rAF-debouncing breaks
    // that loop so every chart reliably follows the window.
    const scheduleResize = () => {
      if (resizeRaf) cancelAnimationFrame(resizeRaf)
      resizeRaf = requestAnimationFrame(() => {
        resizeRaf = null
        if (mountedChart.value) chartRef.value?.resize?.()
        else tryMount()
      })
    }

    const tryMount = async () => {
      if (!mountedChart.value && props.active && canMount()) {
        await nextTick()
        mountedChart.value = true
        await nextTick()
        chartRef.value?.resize?.()
      } else if (mountedChart.value) {
        chartRef.value?.resize?.()
      }
    }

    // When mounted, observe visibility and size
    onMounted(() => {
      // Watch visibility (hidden tab → visible)
      io = new IntersectionObserver((entries) => {
        if (entries[0]?.isIntersecting) tryMount()
      }, { threshold: 0.01 })
      if (wrapperRef.value) io.observe(wrapperRef.value)

      // Watch size changes (debounced — see scheduleResize)
      ro = new ResizeObserver(scheduleResize)
      if (wrapperRef.value) ro.observe(wrapperRef.value)

      // window resize is NOT subject to the RO loop limit, so it's the reliable
      // trigger during a live window drag.
      window.addEventListener('resize', scheduleResize)

      tryMount()
    })

    // When activated again (keep-alive or MDB tab show)
    onActivated(() => {
      if (props.active) tryMount()
    })

    // React to tab visibility changes
    watch(() => props.active, (isActive) => {
      if (isActive) tryMount()
    })

    onBeforeUnmount(() => {
      io?.disconnect()
      ro?.disconnect()
      window.removeEventListener('resize', scheduleResize)
      if (resizeRaf) cancelAnimationFrame(resizeRaf)
    })

    return { wrapperRef, chartRef, mountedChart }
  }
}
</script>

<style scoped>
.chart-wrapper {
  width: 100%;
  position: relative;
  min-width: 0;        /* the canvas must not impose a width floor on ancestors,
                          else flex/grid parents won't shrink on window resize
                          (layout looks "stuck" until a scroll re-fires resize) */
  /* The ECharts canvas is sized in px by JS; without this, the wrapper's width
     depends on the canvas AND the canvas depends on the wrapper — a circular
     loop that freezes the layout on window-drag (it can't shrink past the last
     rendered canvas width). contain:inline-size makes the wrapper's width
     depend ONLY on its container, breaking the loop. Doesn't clip (tooltips
     survive), unlike overflow:hidden. */
  contain: inline-size;
}
.chart {
  width: 100%;
  height: 100%;
  max-width: 100%;
  min-width: 0;
}
.skeleton {
  width: 100%;
  height: 100%;
  background: #f6f8fb;
  border: 1px solid #e8eef7;
  border-radius: 14px;
}
</style>